import React from 'react';
import clsx from 'clsx';
import Select from 'react-select';
import { withTranslation, WithTranslation } from 'react-i18next';
import ThemeSelector from './ThemeSelector';
import { MEASUREMENT_TYPES, FEEDBACK_CATEGORIES } from '../../constants/config';
import { TargetNode } from '../../models/TargetNode';


type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;
// react-select's onChange can hand back null (it types every select as
// clearable), so the parameter has to admit it even though these are not.
type SelectHandler = (option: { value: number } | null) => void;

interface ThemeSettingsPanelOwnProps {
  node: TargetNode;
  // The theme drives the four scalars below: picking one resets them all.
  themeId: number;
  onChangeTheme: SelectHandler;
  measurementTypeId?: number;
  onChangeMeasurementType: SelectHandler;
  categoryId?: number;
  onChangeCategory: SelectHandler;
  iconPrefix: string;
  onChangeIconPrefix: ChangeHandler;
  minValue: number | string;
  onChangeMinValue: ChangeHandler;
  maxValue: number | string;
  onChangeMaxValue: ChangeHandler;
  // Computed from min/max; displayed disabled.
  votingScale: number | string;
  iconsOrder: string;
  onChangeIconsOrder: () => void;
}

// The panel's own data, without the presentational class names. Grouped so the
// parent can forward it as one prop instead of eighteen.
export type ThemeSettings = ThemeSettingsPanelOwnProps;

interface ThemeSettingsPanelProps extends ThemeSettingsPanelOwnProps {
  themeSelectorClassName?: string;
  buttonClassName?: string;
}

// react-select wants the option object. Both lists are keyed by a numeric id.
const optionFor = (options: Array<{ value: number; label: string }>, id?: number) => (
  typeof id === "number" ? { label: options.find(x => x.value === id)!.label, value: id } : null
);

const ThemeSettingsPanel = ({
  node, themeId, onChangeTheme, measurementTypeId, onChangeMeasurementType,
  categoryId, onChangeCategory, iconPrefix, onChangeIconPrefix,
  minValue, onChangeMinValue, maxValue, onChangeMaxValue, votingScale,
  iconsOrder, onChangeIconsOrder,
  themeSelectorClassName, buttonClassName, t,
}: ThemeSettingsPanelProps & WithTranslation) => (
  <>
    <label>{t('target.editor.label.theme')}</label>
    {/* ThemeSelector seeds its own selection from the node and ignores a
        `value` prop, so themeId is not passed — it was silently unused. */}
    <ThemeSelector
      className={themeSelectorClassName}
      node={node}
      onChangeTheme={onChangeTheme} />

    <label>{t('target.editor.label.measurementType')}</label>
    <Select
      placeholder="SELECT..."
      options={MEASUREMENT_TYPES}
      value={optionFor(MEASUREMENT_TYPES, measurementTypeId)}
      onChange={onChangeMeasurementType} />

    <label>{t('target.editor.label.category')}</label>
    <Select
      options={FEEDBACK_CATEGORIES}
      value={optionFor(FEEDBACK_CATEGORIES, categoryId)}
      onChange={onChangeCategory} />

    <label>{t('target.editor.label.iconsPath')}</label>
    <input type="text" data-redisid="iconprefix"
      value={iconPrefix}
      title="Set icons prefix."
      onChange={onChangeIconPrefix} />

    <div style={{ height: "0.4em" }}></div>

    <div>
      <label>{t('target.editor.label.minValue')}</label>
      <input type="number" min="0" max="100" style={{ marginRight: "0.5em" }} data-redisid="min value"
        value={minValue}
        title="Set min value."
        onChange={onChangeMinValue} />
    </div>

    <div>
      <label>{t('target.editor.label.maxValue')}</label>
      <input type="number" min="1" max="100" style={{ marginRight: "0.5em" }} data-redisid="max value"
        value={maxValue}
        title="Set max value."
        onChange={onChangeMaxValue} />
    </div>

    <label>{t('target.editor.label.votingScale')}</label>
    <input disabled type="number" min="1" max="11" style={{ marginRight: "0.5em" }} data-redisid="voting scale"
      value={votingScale}
      title="Set voting scale." />

    <label>{t('target.editor.label.iconsOrder')}</label>
    {/*
      Always `button success`. This used to key its colour off isEnabled — whether
      the TARGET is enabled — which has nothing to do with icon order; it looks
      like copy-paste from the enable button. The button is an action, not a
      state indicator, so it no longer flips to alert.
    */}
    <button className={clsx(buttonClassName, 'button success')}
      type="button" data-redisid="iconsorder"
      title="Select voting icons order"
      onClick={onChangeIconsOrder}>
      <i className={iconsOrder === '<' ? "fa fa-arrow-circle-left" : "fa fa-arrow-circle-right"} aria-hidden="true"></i>
      {iconsOrder === '<' ? t('target.editor.button.reversed') : t('target.editor.button.default')}
    </button>
  </>
);

export default withTranslation('common')(ThemeSettingsPanel);
